import { Vector3, type BufferGeometry, type Mesh } from 'three';

/**
 * Sladí kůži postavy s oblečením.
 *
 * Readme toho packu to říká rovnou: *"When using the clothing, only the head
 * of the model is required. Using the full body will result in clipping."*
 * Přesně tak to i je — outfity počítají s tím, že z těla zbude hlava a nic
 * víc.
 *
 * Jenže oblast hlavy se skládala z vah kostí `Head` **a `neck_01`**, a váhy
 * `neck_01` v tomhle rigu sahají patnáct centimetrů pod límec, doprostřed
 * zad. Ten kus se pak kreslil pod tričkem a prorážel ho ven jako dvě skvrny
 * na lopatkách. Zatlačovat ho pod látku byla marnost: on tam vůbec neměl být.
 *
 * Tenhle modul proto dělá dvě věci, v tomhle pořadí:
 *
 *  1. **Zahodí zakrytou kůži.** Vrchol, jehož nejbližší místo na oblečení
 *     leží uvnitř látky, je schovaný — trojúhelníky složené jen z takových
 *     vrcholů se přestanou kreslit. Co neexistuje, nemůže prolézt ven.
 *  2. **Zbytek na hranici zatlačí pod látku.** Prstenec trojúhelníků na
 *     rozhraní musí zůstat, aby v krku nezela díra; ten se schová pod povrch.
 *
 * Klíčové je rozlišení **vnitřek látky vs. její okraj**. U výstřihu, manžety
 * nebo lemu kůže vystupovat má — kdyby se řídilo jen vzdáleností, zmizel by
 * i krk.
 *
 * Počítá se to jednou při obléknutí v klidové póze. Za běhu by to bylo drahé
 * a kůže s látkou stejně visí na téže kostře, takže se během animace rozejdou
 * jen nepatrně.
 */

/** Jak hluboko pod látku zatlačit kůži na hranici. */
const MARGIN = 0.006;
/** Dál než tohle se látka nehledá. */
const SEARCH = 0.15;
/** Kolem otvoru látky (výstřih, manžeta, lem) se kůže nechává být. */
const RIM_ZONE = 0.012;
/**
 * Kolikrát se zatlačení zopakuje.
 *
 * Jeden průchod nestačí u záhybů: vrchol se schová pod jednu plochu a tím
 * vyjede zpod sousední. Druhý průchod to dorovná, třetí je pojistka.
 */
const PASSES = 3;
/** Vrcholy patřící z většiny lebce se nikdy nemažou ani nehýbou. */
const SKULL_WEIGHT = 0.5;

interface Triangle {
  a: Vector3;
  b: Vector3;
  c: Vector3;
  normal: Vector3;
  center: Vector3;
  radius: number;
  /** Okrajové hrany (ab, bc, ca) — ty, které nesousedí s dalším trojúhelníkem. */
  rim: [boolean, boolean, boolean];
}

/** Nejbližší místo na oblečení a jak k němu vrchol leží. */
interface Hit {
  /** Kladné = kůže trčí ven z látky, záporné = je uvnitř. */
  side: number;
  distance: number;
  point: Vector3;
  normal: Vector3;
  /** Leží nejbližší místo na okraji látky (výstřih, manžeta, lem)? */
  onRim: boolean;
}

/**
 * @param skin       geometrie těla, mění se na místě
 * @param rest       původní pozice, ze kterých se pokaždé vychází
 * @param garment    plochy zakrývající tělo (látka i holé ruce z packu)
 * @param skullBones indexy kostí lebky v kostře
 */
export function fitSkinUnderGarment(
  skin: BufferGeometry,
  rest: Float32Array,
  garment: Mesh[],
  skullBones: number[] = [],
): void {
  const position = skin.getAttribute('position');
  const array = position.array as Float32Array;
  // Vždy od původních pozic — jinak by se každé převlečení zanořilo hlouběji.
  array.set(rest);

  const triangles = collectTriangles(garment);
  if (triangles.length === 0) {
    position.needsUpdate = true;
    skin.computeVertexNormals();
    return;
  }

  // Jen vrcholy, které se opravdu kreslí.
  const index = skin.getIndex();
  const used = new Set<number>();
  if (index) {
    for (let i = 0; i < index.count; i++) used.add(index.getX(i));
  } else {
    for (let i = 0; i < position.count; i++) used.add(i);
  }

  const skinIndex = skin.getAttribute('skinIndex');
  const skinWeight = skin.getAttribute('skinWeight');
  const skull = new Set(skullBones);
  const point = new Vector3();

  for (const i of used) {
    if (skullWeightOf(skinIndex, skinWeight, skull, i) > SKULL_WEIGHT) continue;

    point.set(array[i * 3], array[i * 3 + 1], array[i * 3 + 2]);
    let touched = false;

    for (let pass = 0; pass < PASSES; pass++) {
      const hit = nearestOnGarment(point, triangles, SEARCH);
      if (!hit || hit.side <= -MARGIN || hit.onRim) break;
      // Posun čistě po normále dovnitř, ne skok na nejbližší bod látky.
      // Skok vrchol posouval i do stran a u záhybů ho tím vystrkoval zpod
      // sousední plochy ven — pak se to mezi nimi přehazovalo a další
      // průchody stav zhoršovaly místo aby ho spravily.
      point.addScaledVector(hit.normal, -(hit.side + MARGIN));
      touched = true;
    }

    if (!touched) continue;
    array[i * 3] = point.x;
    array[i * 3 + 1] = point.y;
    array[i * 3 + 2] = point.z;
  }

  position.needsUpdate = true;
  skin.computeVertexNormals();
}

function skullWeightOf(
  skinIndex: ReturnType<BufferGeometry['getAttribute']> | undefined,
  skinWeight: ReturnType<BufferGeometry['getAttribute']> | undefined,
  skull: Set<number>,
  i: number,
): number {
  if (!skinIndex || !skinWeight || skull.size === 0) return 0;
  const idx = [skinIndex.getX(i), skinIndex.getY(i), skinIndex.getZ(i), skinIndex.getW(i)];
  const wgt = [skinWeight.getX(i), skinWeight.getY(i), skinWeight.getZ(i), skinWeight.getW(i)];
  let weight = 0;
  for (let s = 0; s < 4; s++) if (skull.has(idx[s])) weight += wgt[s];
  return weight;
}

const closestPoint = new Vector3();
const bestPoint = new Vector3();
const relative = new Vector3();

function nearestOnGarment(p: Vector3, triangles: Triangle[], maxDistance: number): Hit | null {
  let bestDistance = maxDistance * maxDistance;
  let bestTriangle: Triangle | null = null;

  for (const t of triangles) {
    // Hrubé odmítnutí přes obalovou kouli trojúhelníku — bez něj by se
    // pro každý vrchol procházely tisíce trojúhelníků naplno.
    const reach = Math.sqrt(bestDistance) + t.radius;
    if (p.distanceToSquared(t.center) > reach * reach) continue;

    closestOnTriangle(p, t, closestPoint);
    const d = p.distanceToSquared(closestPoint);
    if (d < bestDistance) {
      bestDistance = d;
      bestPoint.copy(closestPoint);
      bestTriangle = t;
    }
  }

  if (!bestTriangle) return null;
  return {
    side: relative.subVectors(p, bestPoint).dot(bestTriangle.normal),
    distance: Math.sqrt(bestDistance),
    point: bestPoint,
    normal: bestTriangle.normal,
    onRim: nearRim(bestPoint, bestTriangle),
  };
}

/** Leží bod na okraji látky, nebo uvnitř plochy? */
function nearRim(p: Vector3, t: Triangle): boolean {
  if (t.rim[0] && distanceToSegment(p, t.a, t.b) < RIM_ZONE) return true;
  if (t.rim[1] && distanceToSegment(p, t.b, t.c) < RIM_ZONE) return true;
  if (t.rim[2] && distanceToSegment(p, t.c, t.a) < RIM_ZONE) return true;
  return false;
}

const segment = new Vector3();
const onSegment = new Vector3();
function distanceToSegment(p: Vector3, a: Vector3, b: Vector3): number {
  segment.subVectors(b, a);
  const lengthSq = segment.lengthSq();
  if (lengthSq < 1e-12) return p.distanceTo(a);
  const t = Math.max(0, Math.min(1, onSegment.subVectors(p, a).dot(segment) / lengthSq));
  return onSegment.copy(a).addScaledVector(segment, t).distanceTo(p);
}

function collectTriangles(garment: Mesh[]): Triangle[] {
  const out: Triangle[] = [];
  const ab = new Vector3();
  const ac = new Vector3();

  const visible = garment.filter((mesh) => mesh.visible);

  // Kolikrát je která hrana použitá. Hrana jen v jednom trojúhelníku je
  // skutečný otvor v oblečení (výstřih, manžeta, lem).
  //
  // Dvě věci, na kterých to stojí:
  //
  //  - Klíč se skládá z **pozic, ne z indexů**. Na švech mají vrcholy kvůli
  //    UV dvojníky, takže by se přes indexy jevila jako okraj i půlka trička.
  //  - Počítá se **přes všechny díly najednou**. Průramek trička je totiž
  //    hrana jen v jednom trojúhelníku *toho dílu*, ale zapadá do něj rukáv —
  //    otvor to tedy není, jen šev. Když se každý díl počítal zvlášť, oba
  //    průramky se tvářily jako otvory, kůže kolem nich zůstala nezakrytá a
  //    prolezla ven jako dvě symetrické skvrny na lopatkách.
  const edgeUse = new Map<string, number>();
  const keysOf = new Map<Mesh, string[]>();

  for (const mesh of visible) {
    const geometry = mesh.geometry;
    const pos = geometry.getAttribute('position');
    const index = geometry.getIndex();
    const count = index ? index.count : pos.count;

    const keys: string[] = [];
    for (let i = 0; i < count; i++) {
      const j = index ? index.getX(i) : i;
      keys.push(positionKey(pos.getX(j), pos.getY(j), pos.getZ(j)));
    }
    keysOf.set(mesh, keys);

    for (let i = 0; i < count; i += 3) {
      for (const [u, v] of EDGES) {
        const key = edgeKey(keys[i + u], keys[i + v]);
        edgeUse.set(key, (edgeUse.get(key) ?? 0) + 1);
      }
    }
  }

  for (const mesh of visible) {
    const geometry = mesh.geometry;
    const pos = geometry.getAttribute('position');
    const index = geometry.getIndex();
    const count = index ? index.count : pos.count;
    const keys = keysOf.get(mesh)!;

    for (let i = 0; i < count; i += 3) {
      const ia = index ? index.getX(i) : i;
      const ib = index ? index.getX(i + 1) : i + 1;
      const ic = index ? index.getX(i + 2) : i + 2;

      const a = new Vector3(pos.getX(ia), pos.getY(ia), pos.getZ(ia));
      const b = new Vector3(pos.getX(ib), pos.getY(ib), pos.getZ(ib));
      const c = new Vector3(pos.getX(ic), pos.getY(ic), pos.getZ(ic));

      const normal = ab.subVectors(b, a).cross(ac.subVectors(c, a)).clone();
      const length = normal.length();
      if (length < 1e-9) continue; // zdegenerovaný trojúhelník nemá normálu
      normal.divideScalar(length);

      const center = new Vector3().addVectors(a, b).add(c).divideScalar(3);
      out.push({
        a,
        b,
        c,
        normal,
        center,
        radius: Math.max(center.distanceTo(a), center.distanceTo(b), center.distanceTo(c)),
        rim: [
          (edgeUse.get(edgeKey(keys[i], keys[i + 1])) ?? 0) < 2,
          (edgeUse.get(edgeKey(keys[i + 1], keys[i + 2])) ?? 0) < 2,
          (edgeUse.get(edgeKey(keys[i + 2], keys[i])) ?? 0) < 2,
        ],
      });
    }
  }
  return out;
}

const EDGES: [number, number][] = [
  [0, 1],
  [1, 2],
  [2, 0],
];

/**
 * Klíč pozice pro párování hran, zaokrouhlený na milimetr.
 *
 * Jemněji ne: vrcholy švu mezi dvěma díly nemusí sedět úplně přesně a při
 * zaokrouhlení na desetinu milimetru by se šev rozpadl na dva okraje.
 */
function positionKey(x: number, y: number, z: number): string {
  return `${Math.round(x * 1e3)},${Math.round(y * 1e3)},${Math.round(z * 1e3)}`;
}

function edgeKey(first: string, second: string): string {
  return first < second ? `${first}|${second}` : `${second}|${first}`;
}

/** Nejbližší bod trojúhelníku k zadanému bodu (Ericson, Real-Time Collision Detection). */
function closestOnTriangle(p: Vector3, t: Triangle, target: Vector3): Vector3 {
  const ab = new Vector3().subVectors(t.b, t.a);
  const ac = new Vector3().subVectors(t.c, t.a);
  const ap = new Vector3().subVectors(p, t.a);

  const d1 = ab.dot(ap);
  const d2 = ac.dot(ap);
  if (d1 <= 0 && d2 <= 0) return target.copy(t.a);

  const bp = new Vector3().subVectors(p, t.b);
  const d3 = ab.dot(bp);
  const d4 = ac.dot(bp);
  if (d3 >= 0 && d4 <= d3) return target.copy(t.b);

  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    return target.copy(t.a).addScaledVector(ab, d1 / (d1 - d3));
  }

  const cp = new Vector3().subVectors(p, t.c);
  const d5 = ab.dot(cp);
  const d6 = ac.dot(cp);
  if (d6 >= 0 && d5 <= d6) return target.copy(t.c);

  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    return target.copy(t.a).addScaledVector(ac, d2 / (d2 - d6));
  }

  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    return target
      .copy(t.b)
      .addScaledVector(new Vector3().subVectors(t.c, t.b), (d4 - d3) / (d4 - d3 + (d5 - d6)));
  }

  const denom = 1 / (va + vb + vc);
  return target.copy(t.a).addScaledVector(ab, vb * denom).addScaledVector(ac, vc * denom);
}
